import { useCallback, useEffect } from "react";
import { Log, CreateLog, UpdateLog } from "../types/models/Log.d";
import { UpdateEntityLog } from "../types/models/Log";
import { useGetLogListQuery, useInsertLogMutation, useUpdateLogMutation, useDeleteLogMutation, } from "../apis/Log.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function useLog(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertLogMutation, { 
        isLoading: insertLogLoading, 
        isSuccess: insertLogIsSuccess,
        isError: insertLogIsError,
        error: insertLogError,
    }] = useInsertLogMutation()
    const [updateLogMutation, { 
        isLoading: updateLogLoading,
        isSuccess: updateLogIsSuccess,
        isError: updateLogIsError,
        error: updateLogError,
    }] = useUpdateLogMutation()
    const [deleteLogMutation, { 
        isLoading: deleteLogLoading,
        isSuccess: deleteLogIsSuccess,
        isError: deleteLogIsError,
        error: deleteLogError,
    }] = useDeleteLogMutation()

    const loading =
        insertLogLoading ||
        updateLogLoading ||
        deleteLogLoading ||
        false

    const insertLog = useCallback(async (log: CreateLog, redirectPath: string|undefined) => {
        try {
            const data = await insertLogMutation(log).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertLogMutation, navigate])
    
    useEffect(() => {
        if (insertLogIsSuccess) {
            openSuccessAlert('Log created')
        } else if (insertLogIsError) {
            console.log("add error", insertLogError)
            openFailAlert(t`Log add error: ` + insertLogError?.data?.message)
        }
    }, [
        insertLogIsSuccess,
        insertLogIsError,
        insertLogError
    ]);

    const updateLog = useCallback(async (log: UpdateEntityLog, redirectPath: string|undefined) => {
        try {
            const data = await updateLogMutation(log).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updateLogMutation, navigate])
    
    useEffect(() => {
        if (updateLogIsSuccess) {
            openSuccessAlert(t`Log updated`)
        } else if (updateLogIsError) {
            console.log("update error", updateLogError)
            openFailAlert(t`Log update error: ` + updateLogError?.data?.message)
        }
    }, [
        updateLogIsSuccess,
        updateLogIsError,
        updateLogError
    ]);

    const deleteLog = useCallback((log: Log, redirectPath: string|undefined) => {
        openDialog(async () => {
            log = {
                ...log,
            }
            try {
                const { data } = await deleteLogMutation(log);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deleteLogMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deleteLogIsSuccess) {
            openSuccessAlert(t`Log deleted`)
        } else if (deleteLogIsError) {
            console.log("delete error", deleteLogError)
            openFailAlert(t`Log delete error: ` + deleteLogError?.data?.message)
        }
    }, [
        deleteLogIsSuccess,
        deleteLogIsError,
        deleteLogError
    ]);

    return {
        loading,
        insertLog,
        updateLog,
        deleteLog
    }
}
